import { useCallback, useEffect, useRef, useState } from "react";
import { fetchAdminMap } from "../lib/api";
import MaslowSphere from "./MaslowSphere";
import { Pause, Play, SkipBack } from "lucide-react";

const FRAME_MS = 1200;

/** Answer speed painted onto the sphere, with a scrubbable turn-by-turn replay. */
export const RhythmReplay = ({ subject }) => {
  const [data, setData] = useState(null);
  const [frame, setFrame] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(1);
  const [err, setErr] = useState("");
  const timer = useRef(null);

  const load = useCallback(
    () =>
      fetchAdminMap(subject)
        .then((d) => {
          setData(d);
          setFrame(0);
          setPlaying(false);
        })
        .catch((e) => {
          setData(null);
          setErr(e?.response?.data?.detail || "could not load this subject's sphere");
        }),
    [subject]
  );

  useEffect(() => {
    load();
  }, [load]);

  const frames = data?.replay || [];
  const total = frames.length;

  useEffect(() => {
    clearInterval(timer.current);
    if (!playing || total === 0) return;
    timer.current = setInterval(() => {
      setFrame((f) => {
        if (f + 1 >= total) {
          setPlaying(false);
          return f;
        }
        return f + 1;
      });
    }, FRAME_MS / speed);
    return () => clearInterval(timer.current);
  }, [playing, speed, total]);

  const cur = frames[frame];
  const active = cur?.categories || [];
  const slow = (data?.rhythm_by_category || []).length ? data.rhythm_by_category : null;

  return (
    <div data-testid="admin-rhythm-replay" className="space-y-6">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <p className="label">
          Rhythm replay · node brightness and pulse rate are answer latency
        </p>
        <span className="font-data text-[10px] text-zinc-600">
          mean {data?.rhythm_overall?.mean_seconds ?? "—"}s · sd{" "}
          {data?.rhythm_overall?.sd_seconds ?? "—"}s · {total} timed turns
        </span>
      </div>

      <div className="relative h-[26rem] border border-[var(--line)]">
        {data ? (
          <MaslowSphere
            map={data}
            heat={data.heat || {}}
            activeIds={active}
            autoRotate={!playing}
            showCandidates={false}
          />
        ) : (
          <p className="p-5 font-data text-[11px] text-zinc-600">{err || "loading sphere…"}</p>
        )}

        {cur && (
          <div className="panel pointer-events-none absolute left-5 top-5 max-w-[16rem] px-4 py-3">
            <p className="label">
              frame {cur.frame} / {total} · turn {cur.turn ?? "—"}
            </p>
            <p
              data-testid="replay-frame-seconds"
              className="mt-2 font-data text-2xl tabular-nums leading-none text-zinc-100"
            >
              {cur.seconds ?? "—"}s
            </p>
            <p className="mt-2 font-data text-[10px] text-zinc-500">
              {cur.z > 0 ? "+" : ""}
              {cur.z}σ vs own mean · {cur.z >= 1 ? "HESITATION" : cur.z <= -1 ? "fluent" : "typical"}
            </p>
            <p className="mt-2 font-data text-[10px] text-zinc-600">
              {cur.phase || "—"}
              {cur.control_type ? ` · ${cur.control_type}` : ""}
              {cur.authenticity !== null && cur.authenticity !== undefined
                ? ` · auth ${cur.authenticity}`
                : ""}
            </p>
            {cur.category_names?.length > 0 && (
              <p className="mt-2 text-xs leading-relaxed text-zinc-300">
                {cur.category_names.join(", ")}
              </p>
            )}
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3 border border-[var(--line)] px-4 py-4">
        <button
          data-testid="replay-play-btn"
          disabled={total === 0}
          onClick={() => setPlaying((p) => !p)}
          className="btn-stark flex items-center gap-2 !px-3 !py-2"
        >
          {playing ? <Pause size={11} strokeWidth={1.8} /> : <Play size={11} strokeWidth={1.8} />}
          {playing ? "Pause" : "Play"}
        </button>
        <button
          data-testid="replay-restart-btn"
          disabled={total === 0}
          onClick={() => setFrame(0)}
          className="btn-stark flex items-center gap-2 !px-3 !py-2"
        >
          <SkipBack size={11} strokeWidth={1.8} /> Restart
        </button>
        <input
          type="range"
          data-testid="replay-scrubber"
          min={0}
          max={Math.max(0, total - 1)}
          value={frame}
          disabled={total === 0}
          onChange={(e) => {
            setPlaying(false);
            setFrame(Number(e.target.value));
          }}
          className="min-w-[12rem] flex-1 accent-zinc-100"
        />
        <div className="flex items-center gap-1">
          {[0.5, 1, 2, 4].map((s) => (
            <button
              key={s}
              data-testid={`replay-speed-${s}`}
              onClick={() => setSpeed(s)}
              className={`px-2.5 py-1.5 font-data text-[10px] tracking-[0.12em] transition-colors ${
                speed === s ? "bg-zinc-100 text-[var(--void)]" : "text-zinc-500 hover:text-zinc-200"
              }`}
            >
              {s}×
            </button>
          ))}
        </div>
      </div>

      {total === 0 && (
        <p className="font-data text-[11px] text-zinc-600">
          No timed turns yet for this subject. Latency is recorded from the next answer onward.
        </p>
      )}

      {slow && (
        <div>
          <p className="label">Hesitation heat by topic</p>
          <ul className="mt-3">
            {slow.slice(0, 10).map((r) => (
              <li
                key={r.key}
                className="grid grid-cols-[1fr_auto] items-center gap-3 border-b border-[var(--line)] py-2"
              >
                <div className="min-w-0">
                  <p className="truncate text-xs text-zinc-200">{r.label}</p>
                  <div className="mt-1 h-[2px] w-full bg-[var(--line)]">
                    <div
                      className="h-full bg-zinc-300"
                      style={{ width: `${(data.heat?.[r.key] ?? 0) * 100}%` }}
                    />
                  </div>
                </div>
                <span className="font-data text-[10px] tabular-nums text-zinc-500">
                  {r.mean_seconds}s · n{r.samples}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default RhythmReplay;
